import { useCallback } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { noop } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import Hidden from '@gef-ui/components/atoms/SvgIcon/Icons/Hidden';
import Visible from '@gef-ui/components/atoms/SvgIcon/Icons/Visible';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { AvatarHead } from 'components/atoms/CustomSVGIcon';
import MODALS from 'constants/modals';
import campaignDetail from 'features/views/campaignSettings/campaignDetail';
import guarantors from 'features/views/campaignSettings/guarantors';

const { createGuarantorCardText } = guarantors.utils;

export const GuarantorsCard = ({ region, campaignId }) => {
	const dispatch = useDispatch();

	const detail = useSelector(campaignDetail.selectors.getDetail);
	const { channelId } = detail;

	const handleOpenEditGuarantorModal = useCallback(
		() => NiceModal.show(MODALS.edit_guarantor, { region, channelId, campaignId }),
		[region, channelId, campaignId],
	);

	const handleHover = useCallback(() => {
		dispatch(guarantors.actions.setHoveredRegionId(region.id));
	}, [dispatch, region]);

	const handleLeave = useCallback(() => {
		dispatch(guarantors.actions.setHoveredRegionId(null));
	}, [dispatch]);

	const handleChangeOfRegion = useCallback(() => {
		if (region.campaigned) {
			dispatch(guarantors.actions.deactivateRegion(campaignId, region));
		} else {
			dispatch(guarantors.actions.activateRegion(campaignId, region));
		}
	}, [dispatch, campaignId, region]);

	const { name, guarantors: guarantorsFromRegion = [], campaigned } = region;
	const disabled = !campaigned;

	return (
		<Card
			sx={{ borderRadius: '5px', boxShadow: '0 0 13px 0 rgba(0,0,0,.24)', height: '100px' }}
			onMouseEnter={handleHover}
			onMouseLeave={handleLeave}
		>
			<CardHeader
				sx={{
					py: 0,
					px: 1,
					backgroundColor: disabled ? 'grey.400' : 'info.shades.50p',
					// needed because of typography noWrap
					'& .MuiCardHeader-content': {
						width: '100%',
					},
				}}
				title={
					<Stack
						direction="row"
						sx={{
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Typography
							noWrap
							sx={{
								fontSize: 15,
								color: 'common.white',
								p: 0.5,
							}}
						>
							{name}
						</Typography>
						<IconButton sx={{ ml: 1 }} color="white" size="small" onClick={handleChangeOfRegion}>
							{disabled ? <Hidden /> : <Visible />}
						</IconButton>
					</Stack>
				}
			/>
			<CardContent sx={{ py: 1 }}>
				<Stack
					direction="row"
					sx={{
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Stack
						direction="row"
						sx={{
							alignItems: 'center',
							height: '40px',
						}}
					>
						{guarantorsFromRegion.length === 1 && (
							<IconButton sx={{ color: 'grey.600', height: '40px', pl: 0 }} disableRipple>
								<AvatarHead />
							</IconButton>
						)}
						<Typography variant="body2">{createGuarantorCardText(guarantorsFromRegion)}</Typography>
					</Stack>
					<Typography
						sx={{
							cursor: 'pointer',
							color: disabled ? 'grey.400' : 'primary.main',
						}}
						variant="h6"
						onClick={region.campaigned ? handleOpenEditGuarantorModal : noop}
					>
						{getLabelMessage('guarantors:edit')}
					</Typography>
				</Stack>
			</CardContent>
		</Card>
	);
};

GuarantorsCard.propTypes = {
	campaignId: PropTypes.number,
	region: PropTypes.object,
};

export default GuarantorsCard;
